// Checklist del preoperacional diario. La estructura y vocabulario de
// respuestas (Bueno/Malo, Funciona/No funciona, etc.) están transcritos de
// un PDF REAL generado por el sistema actual (preop_GKV827_20260729...pdf,
// formato FT-TRP-064 Grúa Canasta) — esa es la fuente de verdad del proceso
// vigente (diario, no semanal). Los demás tipos de vehículo se completan
// con los ítems de las plantillas oficiales FT-MTR-065/066/067/072
// (mismos nombres de ítem, adaptados a este mismo formato diario).
export const PREOP_FORMAT_TITLE = 'Preoperacional';
export const PREOP_FORMAT_VERSION = '06';

// Bloque de responsables que deben autorizar y firmar cada preoperacional
// (capturado en el mismo dispositivo del conductor, igual que en el PDF
// real). El nombre queda vacío hasta que cada responsable firme desde su
// propia cuenta (preoperationals.php sign_authorization lo llena con el
// usuario conectado). Antes venían nombres fijos ("Camilo Ramírez" para
// Seguridad Física, etc.) que aparecían en TODAS las sedes como si esa
// persona fuera a firmar — así salía un vigilante de otra sede en el
// formulario de firma (429 preoperacionales desde sep-2026).
export const PREOP_AUTHORIZATION_BLOCK = [
  { key: 'sst', role: 'COORDINADOR SST', name: '' },
  { key: 'seguridad', role: 'SEGURIDAD FISICA', name: '' },
  { key: 'transportes', role: 'COORDINADOR AREA TRANSPORTES', name: '' },
];

// Motocicletas usan el formato oficial FOR-SI-64, que solo pide firma de
// Conductor + Supervisor (no los 3 responsables de camionetas/camiones/
// grúas). "supervisor" reutiliza por defecto la misma cuenta autorizada de
// Transportes hasta que se defina un responsable distinto.
export const PREOP_MOTO_AUTHORIZATION_BLOCK = [
  { key: 'supervisor', role: 'SUPERVISOR', name: '' },
];

const item = (key, label, options) => ({ key, label, options });
const bm = ['Bueno', 'Malo'];
const bmF = ['Buena', 'Mala'];
const bmPl = ['Buenos', 'Malos'];
const bmPlF = ['Buenas', 'Malas'];
const func = ['Funciona', 'No funciona'];
const funcPl = ['Funcionan', 'No funcionan'];
const correcta = ['Correcta', 'Incorrecta'];
const siNo = ['Si', 'No'];
const completo = ['Completo', 'Incompleto'];
const completoPl = ['Completas', 'Incompletas'];
const conformeNc = ['Conforme', 'No Conforme', 'No Aplica'];
const nivelVML = ['Lleno', 'Medio', 'Vacío'];

// ---- Secciones comunes (con pequeñas variaciones por tipo donde aplica) ----

const VIDRIOS_ESPEJOS = [
  item('parabrisas', 'Parabrisas', bm),
  item('limpiaparabrisas', 'Limpiaparabrisas', funcPl),
  item('espejos_laterales', 'Espejos laterales', bmPl),
];

const LUCES = [
  item('luces_bajas', 'Bajas', funcPl),
  item('luces_altas', 'Altas', funcPl),
  item('luces_direccionales', 'Direccionales', funcPl),
  item('luces_estacionarias', 'Estacionarias', funcPl),
  item('luces_cabina', 'Luces de cabina', funcPl),
  item('luz_freno', 'Luz de freno', func),
  item('luces_exploradoras', 'Exploradoras', funcPl),
];

const EQUIPO_EMERGENCIA = [
  item('kit_derrames', 'Kit de derrames', ['Bueno', 'Malo', 'No aplica']),
  item('botiquin', 'Botiquín', completo),
  item('conos', 'Conos', bmPl),
  item('gato', 'Gato', bm),
  item('palanca_gato', 'Palanca gato', bmF),
  item('tacos', 'Tacos', bmPl),
  item('llave_pernos', 'Llave de pernos', bmF),
  item('paletas_pare_siga', 'Paletas de pare y siga', bmPlF),
  item('herramientas_basicas', 'Herramientas básicas', completoPl),
];

const EXTINTOR = {
  items: [
    item('extintor_pin_seguridad', 'Pin de seguridad', ['Tiene', 'No tiene']),
    item('extintor_cargado', 'Cargado', siNo),
    item('extintor_vigente', 'Vigente', siNo),
  ],
  dateField: { key: 'extintor_fecha_vigencia', label: 'Fecha de vigencia' },
};

const CABINA_BASE = [
  item('tablero_instrumentos', 'Tablero de instrumentos', func),
  item('estado_cojineria', 'Estado de cojinería', bm),
  item('estado_tapiceria', 'Estado de tapicería', bm),
  item('aseo_vehiculo', 'Aseo del vehículo', ['Limpio', 'Sucio']),
  item('manija_puertas', 'Manija de puertas', bmPlF),
  item('chapa_puertas', 'Chapa de puertas', bmPlF),
  item('seguro_puertas', 'Seguro de puertas', funcPl),
  item('eleva_vidrios', 'Eleva-vidrios', funcPl),
  item('espejo_retrovisor_interno', 'Espejo retrovisor interno', bm),
  item('bocina', 'Bocina (Pito)', func),
  item('cinturones_seguridad', 'Cinturones de seguridad', bmPl),
];

const MOTOR_FLUIDOS_BASE = [
  item('nivel_aceite', 'Nivel de aceite', bm),
  item('nivel_refrigerante', 'Nivel de refrigerante', bm),
  item('nivel_hidraulico_direccion', 'Nivel de hidráulico de dirección', bm),
  item('correa_accesorios', 'Correa de accesorios', bmF),
  item('funcionamiento_motor', 'Funcionamiento del motor', ['Normal', 'Anormal']),
  item('tacometros', 'Tacómetros', funcPl),
  item('aceite_caja_cambios', 'Aceite de caja de cambios', bm),
  item('aceite_caja_direccion', 'Aceite de caja de dirección', bm),
  item('liquido_bomba_embrague', 'Líquido de bomba de embrague', bm),
];

const MOTOR_FLUIDOS_CON_FRENOS = [
  ...MOTOR_FLUIDOS_BASE.slice(0, 8),
  item('liquido_frenos', 'Líquido de frenos', bm),
  item('liquido_bomba_embrague', 'Líquido de bomba de embrague', bm),
];

const buildTirePresure = (positions) => positions.map((label, i) => item(`llanta_presion_${i + 1}`, label, correcta));
const buildTireWear = (count) => Array.from({ length: count }, (_, i) => item(`posicion_${i + 1}`, `Posición número ${i + 1}`, bm));

const PRESION_5 = ['Llanta delantera derecha', 'Llanta delantera izquierda', 'Llanta trasera derecha', 'Llanta trasera izquierda', 'Llanta de repuesto'];

// ---- Equipo hidráulico (solo Canasta y Fuerza) ----

const HIDRAULICO_CANASTA = [
  item('hid_nivel_aceite', 'Nivel de aceite', bm),
  item('hid_gatos', 'Gatos', bmPl),
  item('hid_tornamesa', 'Tornamesa', bmF),
  item('hid_estabilizadores', 'Estabilizadores', funcPl),
  item('hid_toma_fuerza', 'Sistema del toma fuerza', func),
  item('hid_bomba_mangueras', 'Bomba hidráulica y mangueras', bmPl),
  item('hid_alarma_estabilizadores', 'Alarma de estabilizadores', func),
  item('hid_estado_estructural', 'Estado estructural', bm),
  item('hid_conector_giratorio', 'Conector giratorio', bm),
  item('hid_grillete_perno', 'Grillete de perno roscado', bm),
  item('hid_estado_brazo', 'Estado del brazo', bm),
  item('hid_extensiones_brazo', 'Estado extensiones del brazo', bm),
  item('hid_controles_justin', 'Controles del justin', funcPl),
  item('hid_estado_vaso', 'Estado del vaso', bm),
  item('hid_estado_lainer', 'Estado del lainer', bm),
  item('hid_mandos_manuales', 'Mandos manuales', funcPl),
];

const HIDRAULICO_FUERZA = [
  item('hid_nivel_aceite', 'Nivel de aceite', bm),
  item('hid_gatos', 'Gatos', bmPl),
  item('hid_tornamesa', 'Tornamesa', bmF),
  item('hid_estabilizadores', 'Estabilizadores', funcPl),
  item('hid_toma_fuerza', 'Sistema del toma fuerza', func),
  item('hid_bomba_mangueras', 'Bomba hidráulica y mangueras', bmPl),
  item('hid_alarma_estabilizadores', 'Alarma de estabilizadores', func),
  item('hid_estado_estructural', 'Estado estructural', bm),
  item('hid_ganchos_seguros', 'Ganchos, seguros, espigos y tuercas', bmPl),
  item('hid_conector_giratorio', 'Conector giratorio', bm),
  item('hid_grillete_perno', 'Grillete de perno roscado', bm),
  item('hid_estado_brazo', 'Estado del brazo', bm),
  item('hid_extensiones_brazo', 'Estado extensiones del brazo', bm),
  item('hid_mandos_manuales', 'Mandos manuales', funcPl),
];

// ---- Otros Componentes (varía según carrocería) ----

const OTROS_GRUA = [
  item('estado_carroceria', 'Estado de carrocería', bm),
  item('estado_carpa', 'Estado de carpa', ['Bueno', 'Malo', 'No aplica']),
  item('parachoque_delantero', 'Parachoque delantero', bm),
  item('parachoque_trasero', 'Parachoque trasero', bm),
  item('alarma_reversa', 'Alarma de reversa', func),
];

const OTROS_CAMIONETA = [
  item('estado_carroceria', 'Estado de carrocería', bm),
  item('estado_carpa', 'Estado de carpa', ['Bueno', 'Malo', 'No aplica']),
  item('parachoque_delantero', 'Parachoque delantero', bm),
  item('parachoque_trasero', 'Parachoque trasero', bm),
  item('portaescaleras', 'Portaescaleras', ['Bueno', 'Malo', 'No aplica']),
  item('puerta_trasera', 'Puerta trasera', bm),
  item('chapa_puerta_trasera', 'Chapa de puerta trasera', bm),
  item('alarma_reversa', 'Alarma de reversa', func),
];

const OTROS_CAMPERO = [
  item('estado_carroceria', 'Estado de carrocería', bm),
  item('parachoque_delantero', 'Parachoque delantero', bm),
  item('parachoque_trasero', 'Parachoque trasero', bm),
  item('puerta_trasera', 'Puerta trasera', bm),
  item('chapa_puerta_trasera', 'Chapa de puerta trasera', bm),
  item('alarma_reversa', 'Alarma de reversa', func),
];

function buildSections({ motorFluidos, hidraulico, otros, tirePresure, tireWearCount }) {
  const sections = [
    { key: 'motor_fluidos', title: 'Sistema de Motor / Fluidos', items: motorFluidos },
  ];
  if (hidraulico) {
    sections.push({ key: 'equipo_hidraulico', title: 'Equipo Hidráulico', items: hidraulico });
  }
  sections.push(
    { key: 'vidrios_espejos', title: 'Vidrios / Espejos', items: VIDRIOS_ESPEJOS },
    { key: 'cabina', title: 'Cabina', items: CABINA_BASE },
    { key: 'luces', title: 'Luces', items: LUCES },
    { key: 'equipo_emergencia', title: 'Equipo de Emergencia', items: EQUIPO_EMERGENCIA },
    { key: 'extintor', title: 'Extintor', items: EXTINTOR.items, dateField: EXTINTOR.dateField },
    { key: 'neumaticos_presion', title: 'Neumáticos Presión', items: buildTirePresure(tirePresure) },
    { key: 'neumaticos_desgaste', title: 'Neumáticos Desgaste', items: buildTireWear(tireWearCount) },
    { key: 'otros_componentes', title: 'Otros Componentes', items: otros },
  );
  return sections;
}

// ---- Motocicleta (formato oficial FOR-SI-64 v2, adaptado a envío diario
// en vez de la matriz mensual del PDF original — mismos ítems y palabras) ----

const MOTO_ESTADO_GENERAL = [
  item('llantas_rines', 'Estado general de llantas y rines (labrado y presión)', conformeNc),
  item('espejos_retrovisores', 'Espejos retrovisores izquierdo y derecho', conformeNc),
  item('cojineria_asientos', 'Cojinería y asientos', conformeNc),
  item('tanque_combustible', 'Estado del tanque de combustible', conformeNc),
  item('tubo_escape', 'Tubo de escape / exhosto', conformeNc),
  item('reposapies', 'Reposapiés', conformeNc),
];

const MOTO_ESTADO_MECANICO = [
  item('limpieza_general', 'Limpieza general', conformeNc),
  item('funcionamiento_cloche', 'Funcionamiento del cloche', conformeNc),
  item('funcionamiento_frenos', 'Funcionamiento de frenos', conformeNc),
  item('funcionamiento_acelerador', 'Funcionamiento acelerador', conformeNc),
  item('estado_guayas', 'Estado de guayas', conformeNc),
  item('sin_fuga_liquidos', 'Sin fuga de líquidos (gasolina, aceite)', conformeNc),
  item('funcionamiento_direccion', 'Funcionamiento dirección', conformeNc),
  item('estado_bateria', 'Estado de batería', conformeNc),
  item('amortiguadores', 'Amortiguadores', conformeNc),
  item('nivel_liquido_frenos', 'Nivel líquido de frenos', nivelVML),
  item('nivel_combustible', 'Nivel combustible', nivelVML),
  item('nivel_aceite', 'Nivel de aceite', nivelVML),
];

const MOTO_SISTEMAS_ACCESORIOS = [
  item('luces_altas_bajas', 'Luces altas y bajas', conformeNc),
  item('luces_direccionales_delanteras', 'Luces direccionales delanteros', conformeNc),
  item('luces_direccionales_traseras', 'Luces direccionales traseros', conformeNc),
  item('stop_luz_freno', 'Stop y luz de freno', conformeNc),
  item('velocimetro_odometro', 'Velocímetro / odómetro', conformeNc),
  item('encendido', 'Encendido', conformeNc),
  item('testigos_tablero', 'Testigos de tablero', conformeNc),
  item('pito_bocina', 'Pito o bocina', conformeNc),
  item('herramientas_basicas', 'Herramientas básicas', conformeNc),
  item('impermeable', 'Impermeable', conformeNc),
  item('casco_certificado', 'Casco certificado (marcado con N° placa)', conformeNc),
  item('coderas', 'Coderas', conformeNc),
  item('rodilleras', 'Rodilleras', conformeNc),
  item('guantes', 'Guantes', conformeNc),
];

const MOTO_SECTIONS = [
  { key: 'estado_general_moto', title: 'Estado General del Vehículo', items: MOTO_ESTADO_GENERAL },
  { key: 'estado_mecanico_moto', title: 'Estado Mecánico del Vehículo', items: MOTO_ESTADO_MECANICO },
  { key: 'sistemas_accesorios_moto', title: 'Estado de Sistemas y Accesorios', items: MOTO_SISTEMAS_ACCESORIOS },
];

// ---- Configuración por tipo de vehículo ----
// vehicle_type en la tabla `vehicles` usa: Fuerza, Canasta, Turbo, Camioneta,
// Motocicleta. "Campero" y cualquier tipo sin formato propio usan Camioneta.
export const PREOP_TYPE_CONFIGS = {
  Canasta: {
    formatCode: 'FT-TRP-064',
    formatLabel: 'Preoperacional Grúa Canasta',
    mileageLabel: 'Horómetro',
    sections: buildSections({
      motorFluidos: MOTOR_FLUIDOS_BASE,
      hidraulico: HIDRAULICO_CANASTA,
      otros: OTROS_GRUA,
      tirePresure: PRESION_5,
      tireWearCount: 5,
    }),
  },
  Fuerza: {
    formatCode: 'FT-TRP-065',
    formatLabel: 'Preoperacional Grúa de Fuerza',
    mileageLabel: 'Horómetro',
    sections: buildSections({
      motorFluidos: MOTOR_FLUIDOS_CON_FRENOS,
      hidraulico: HIDRAULICO_FUERZA,
      otros: OTROS_GRUA,
      tirePresure: [...PRESION_5.slice(0, 4), 'Llanta de repuesto'],
      tireWearCount: 11,
    }),
  },
  Camioneta: {
    formatCode: 'FT-TRP-066',
    formatLabel: 'Preoperacional Camioneta',
    mileageLabel: 'Kilometraje',
    sections: buildSections({
      motorFluidos: MOTOR_FLUIDOS_CON_FRENOS,
      hidraulico: null,
      otros: OTROS_CAMIONETA,
      tirePresure: PRESION_5,
      tireWearCount: 5,
    }),
  },
  Turbo: {
    formatCode: 'FT-TRP-067',
    formatLabel: 'Preoperacional Turbo',
    mileageLabel: 'Kilometraje',
    sections: buildSections({
      motorFluidos: MOTOR_FLUIDOS_CON_FRENOS,
      hidraulico: null,
      otros: OTROS_GRUA,
      tirePresure: PRESION_5,
      tireWearCount: 5,
    }),
  },
  Campero: {
    formatCode: 'FT-TRP-072',
    formatLabel: 'Preoperacional Campero',
    mileageLabel: 'Kilometraje',
    sections: buildSections({
      motorFluidos: MOTOR_FLUIDOS_CON_FRENOS,
      hidraulico: null,
      otros: OTROS_CAMPERO,
      tirePresure: PRESION_5,
      tireWearCount: 5,
    }),
  },
  Motocicleta: {
    formatCode: 'FOR-SI-64',
    formatLabel: 'Inspección Pre-Operacional de Motocicleta',
    mileageLabel: 'Kilometraje',
    authRoles: PREOP_MOTO_AUTHORIZATION_BLOCK,
    sections: MOTO_SECTIONS,
  },
};

export function getPreopConfigForVehicleType(vehicleType) {
  const key = String(vehicleType || '').trim();
  const config = PREOP_TYPE_CONFIGS[key] || PREOP_TYPE_CONFIGS.Camioneta;
  return config.authRoles ? config : { ...config, authRoles: PREOP_AUTHORIZATION_BLOCK };
}

export function createPreopChecklistState(vehicleType) {
  const config = getPreopConfigForVehicleType(vehicleType);
  return config.sections.map((section) => ({
    key: section.key,
    title: section.title,
    items: section.items.map((it) => ({ key: it.key, label: it.label, value: '' })),
    date_value: section.dateField ? '' : undefined,
  }));
}

export function countPendingItems(sectionsState) {
  let pending = 0;
  (sectionsState || []).forEach((sec) => {
    (sec.items || []).forEach((it) => { if (!it.value) pending += 1; });
  });
  return pending;
}

export function countFailures(sectionsState) {
  const failWords = new Set(['malo', 'mala', 'malos', 'malas', 'no funciona', 'no funcionan', 'incorrecta', 'incompleto', 'incompleta', 'sucio', 'anormal', 'no tiene', 'no', 'no conforme', 'vacío', 'vacio']);
  let count = 0;
  (sectionsState || []).forEach((sec) => {
    (sec.items || []).forEach((it) => {
      if (failWords.has(String(it.value || '').trim().toLowerCase())) count += 1;
    });
  });
  return count;
}
